import { all, call, fork, put, take } from "redux-saga/effects";
import {
  fetchLoginApi,
  loginWithTokenApi,
  logoutApi,
  resetPasswordApi,
  signupApi,
  updateUserApi,
} from "src/apiService/modules/system";
import { User } from "src/interfaces/systemState.interface";
import { fbq, MetaPixelId, metaSignUpEventId } from "src/utils/meta";
import {
  clearStoredAttribution,
  getStoredAttribution,
} from "src/utils/attribution";

import { FAIL, START, SUCCESS } from "../common";
import {
  FETCH_LOGIN,
  LOGIN_WITH_TOKEN,
  LOG_OUT,
  RESET_PASSWORD,
  SIGNUP,
  UPDATE_USER,
} from "./actions";

interface LoginResponse {
  token: string;
  uid: string;
  user: User;
}

const FIREBASE_DUPLICATE_SIGNUP_ERROR = "auth/email-already-in-use";
const FUNCTIONS_ALREADY_EXISTS = "functions/already-exists";
const FIREBASE_USER_NOT_FOUND_ERROR = "auth/user-not-found";
function* login() {
  while (true) {
    const { payload } = yield take(FETCH_LOGIN + START);
    try {
      const data: LoginResponse = yield call(
        fetchLoginApi,
        payload.email,
        payload.password
      );
      fbq("init", MetaPixelId, { em: payload.email });
      yield put({ type: FETCH_LOGIN + SUCCESS, payload: data });
    } catch (error: any) {
      yield put({ type: FETCH_LOGIN + FAIL, payload: error.message });
    }
  }
}
function* tokenLogin() {
  while (true) {
    const { payload } = yield take(LOGIN_WITH_TOKEN + START);
    try {
      const data: LoginResponse = yield call(loginWithTokenApi, payload);
      yield put({ type: FETCH_LOGIN + SUCCESS, payload: data });
    } catch (error: any) {
      yield put({ type: FETCH_LOGIN + FAIL, payload: error.message });
    }
  }
}

function* signup() {
  while (true) {
    const { payload } = yield take(SIGNUP + START);
    try {
      const data: LoginResponse = yield call(
        signupApi,
        payload.email,
        payload.password,
        getStoredAttribution()
      );
      clearStoredAttribution();
      fbq("init", MetaPixelId, { em: payload.email });
      fbq(
        "track",
        "InitiateCheckout",
        {},
        { eventID: metaSignUpEventId(data.uid) }
      );
      yield put({ type: SIGNUP + SUCCESS, payload: data });
    } catch (error: any) {
      let message: string = error.message;
      if (
        error.code === FIREBASE_DUPLICATE_SIGNUP_ERROR ||
        error.code === FUNCTIONS_ALREADY_EXISTS
      ) {
        message =
          "The email address you entered is already registered. Log in or enter a different email address.";
      }
      yield put({ type: SIGNUP + FAIL, payload: message });
    }
  }
}

function* resetPassword() {
  while (true) {
    const { payload } = yield take(RESET_PASSWORD + START);
    try {
      yield call(resetPasswordApi, payload.email);
      yield put({ type: RESET_PASSWORD + SUCCESS });
    } catch (error: any) {
      let message: string = error.message;
      if (error.code === FIREBASE_USER_NOT_FOUND_ERROR) {
        message =
          "The email address you entered is incorrect or the account does not exist";
      }
      yield put({ type: RESET_PASSWORD + FAIL, payload: message });
    }
  }
}

function* updateUser() {
  while (true) {
    const { payload } = yield take(UPDATE_USER + START);
    const updatedPayload: any = {
      pm_id: payload.pm_id || null,
      plan: payload?.plan || null,
      coupon: payload.coupon || null,
      income_tax_rate: payload.income_tax_rate || null,
      customer_id: payload.customer_id || null,
    };

    try {
      yield call(updateUserApi, updatedPayload);
      yield put({ type: UPDATE_USER + SUCCESS });
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message;
      yield put({ type: UPDATE_USER + FAIL, payload: message });
    }
  }
}

function* logout() {
  while (true) {
    yield take(LOG_OUT);
    yield logoutApi();
  }
}

export function* appSagas() {
  yield all([
    fork(login),
    fork(tokenLogin),
    fork(signup),
    fork(resetPassword),
    fork(logout),
    fork(updateUser),
  ]);
}
